<template>
  <Overlay
    type="dialog"
    :open="open"
    :style="`--width: 800px; --sl-z-index-dialog: 500`"
    @request-close="open = false"
  >
    <div slot="label" class="customerSummary__header">
      <PhEyeglasses :size="20" />
      <span>{{ $t('customer.summary.title') }}</span>
      <span class="customerSummary__greeting">{{ greeting }}</span>
    </div>
    <div class="customerSummary">
      <div class="customerSummary__grid">
        <div class="customerSummary__card">
          <CustomerSummaryInfo :info="info" :loading="isLoading" :error="hasError" />
        </div>
        <div class="customerSummary__card">
          <CustomerSummaryPrescription
            :prescription="prescription"
            :loading="isLoading"
            :error="hasError"
          />
        </div>
        <div class="customerSummary__card">
          <CustomerSummaryLastOrder
            :order="lastOrder"
            :loading="isLoading"
            :error="hasError"
            @open-order="open = false"
          />
        </div>
        <div class="customerSummary__card">
          <CustomerSummaryAfterSales
            :groups="afterSales"
            :insurances="insurances"
            :claims="insuranceClaims"
            :loading="isLoading"
            :error="hasError"
          />
        </div>
      </div>
      <!-- Class goes on the component, not a wrapper: ProductRecommendations renders no root
           at all when the customer has no optical picks, so the card chrome disappears with
           it instead of leaving an empty highlighted box. -->
      <ProductRecommendations
        class="customerSummary__recommendations"
        :customer-email="customerEmail"
        :active="open"
        @close="open = false"
      />
    </div>
  </Overlay>
</template>

<script setup>
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDeviceStore } from '@/stores/device.js'
import { useCustomerSummary } from '@/composables/useCustomerSummary.js'
import { PhEyeglasses } from '@phosphor-icons/vue'

import Overlay from '@/components/shared/Overlay/Overlay.vue'
import ProductRecommendations from '@/components/shared/ProductRecommendations/ProductRecommendations.vue'
import CustomerSummaryInfo from './CustomerSummaryInfo.vue'
import CustomerSummaryPrescription from './CustomerSummaryPrescription.vue'
import CustomerSummaryLastOrder from './CustomerSummaryLastOrder.vue'
import CustomerSummaryAfterSales from './CustomerSummaryAfterSales.vue'

const props = defineProps({
  customerId: {
    type: String,
    default: null
  },
  customerEmail: {
    type: String,
    default: null
  }
})

const open = defineModel('open', {
  type: Boolean,
  default: false
})

const { t } = useI18n()
const deviceStore = useDeviceStore()

const {
  info,
  prescription,
  lastOrder,
  afterSales,
  insurances,
  insuranceClaims,
  isLoading,
  hasError,
  loadCustomerSummary
} = useCustomerSummary()

const greeting = computed(() =>
  t('customer.summary.greeting', { store: deviceStore.settings.name })
)

watch(open, (value) => {
  if (value) {
    loadCustomerSummary(props.customerId)
  }
})
</script>

<style scoped>
.customerSummary {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 4px 0;

  &__header {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__greeting {
    margin-left: auto;
    font-size: var(--sl-font-size-small);
    color: var(--sl-color-neutral-500);
    font-weight: var(--sl-font-weight-normal);
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  &__card {
    background: var(--sl-color-primary-50);
    border-radius: var(--sl-border-radius-medium);
    padding: 12px 14px;
  }

  &__recommendations {
    padding: 12px 14px;
    border: 1px solid var(--sl-color-primary-200);
    border-radius: var(--sl-border-radius-medium);
    background: var(--sl-color-primary-50);
  }

}
</style>
